import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Sun, ShieldCheck, Percent, Bank, HandCoins, UserList, Warning,
  TrayArrowDown, ClockCountdown, ArrowsClockwise, SealQuestion, Scales, CheckCircle, CaretRight,
  Sparkle, WarningCircle, SealCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useHousehold } from "@/context/HouseholdContext";
import { api } from "@/lib/api";
import {
  centsToUSD, buildFactLabels, factLabel, trustTone, toneClasses, prettyFact,
  formatAPUValue, trustLabel, eventLabel, errorLabel,
  reconciledTone, overdueTone, contactStateView, fmtDate, daysAgo,
} from "@/lib/morning";
import { HeroTile } from "@/components/morning/HeroTile";
import { ListDialog } from "@/components/morning/ListDialog";
import { ProvenanceDrawer } from "@/components/morning/ProvenanceDrawer";
import { StatusStrip } from "@/components/morning/StatusStrip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

const scrollToSection = (testid) => {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Chip = ({ tone, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${toneClasses(tone)}`}>
    {tone !== "muted" && tone !== "neutral" && <span className={`h-1.5 w-1.5 rounded-full ${toneClasses(tone).split(" ")[0]}`} />}
    {children}
  </span>
);

const Section = ({ icon: Icon, title, desc, count, children, empty, testid }) => (
  <section data-testid={testid} className="bg-card border border-border rounded-xl overflow-hidden">
    <div className="px-5 py-4 border-b border-border flex items-start gap-3">
      <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0">
        <Icon size={17} className="text-[hsl(var(--accent))]" />
      </div>
      <div className="min-w-0">
        <h2 className="text-base font-semibold flex items-center gap-2">{title}
          {count > 0 && <span className="text-xs font-bold text-muted-foreground tabular-nums">{count}</span>}
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
    </div>
    {count === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">{empty}</p>
      : <div className="divide-y divide-border">{children}</div>}
  </section>
);

const RowItem = ({ onClick, left, right, testid }) => (
  <div onClick={onClick} data-testid={testid}
    className={`px-5 py-3 flex items-center gap-4 ${onClick ? "cursor-pointer hover:bg-[hsl(36_30%_96%)] transition-colors" : ""}`}>
    <div className="min-w-0 flex-1">{left}</div>
    <div className="shrink-0 flex items-center gap-3">{right}{onClick && <CaretRight size={14} className="text-muted-foreground" />}</div>
  </div>
);

const attributionSnippet = (a) => {
  // Mapping-derived rows carry no comms evidence — label them honestly
  // instead of "No snippet extracted" (OA-146 / arena R3).
  if (a.signals?.mapping_id) {
    const decidedBy = a.mapping?.decided_by || "operator";
    return `Matched by your mapping decision (${decidedBy})`;
  }
  const comm = (a.enriched?.comms_apus || [])[0] || {};
  return comm.source?.raw_text || "";
};

const messageLine = (msg) => {
  const parts = [msg.subject || "comms evidence", msg.counterparty_name, msg.channel].filter(Boolean);
  return parts.join(" · ");
};

const attributionState = (a) => {
  if ((a.enriched?.tie_candidates || []).length > 1 || a.state === "ambiguous") {
    return { phrase: "Two possible properties", tone: "warning", label: "ambiguous", Icon: WarningCircle };
  }
  if (a.state === "suggested") {
    const prop = a.enriched?.candidate_property || {};
    return { phrase: `Attribute to ${prop.name || prop.address || a.candidate_property_id || "property"}`, tone: "warning", label: `${((a.confidence || 0) * 100).toFixed(0)}%`, Icon: Sparkle };
  }
  return { phrase: "Needs your attribution", tone: "muted", label: "review", Icon: SealCheck };
};

const OPEN_ATTRIBUTION_STATES = ["suggested", "ambiguous", "review"];

export default function MorningCheck() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hid, assets } = useHousehold();
  const [summary, setSummary] = useState(null);
  const [factLabels, setFactLabels] = useState({});
  const [factTypes, setFactTypes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [allES, setAllES] = useState([]);
  const [events, setEvents] = useState([]);
  const [recs, setRecs] = useState([]);
  const [suggestedDetailed, setSuggestedDetailed] = useState([]);
  const [attributions, setAttributions] = useState([]);
  const [uncategorized, setUncategorized] = useState([]);
  const [adapterErrors, setAdapterErrors] = useState([]);
  const [reconciledApus, setReconciledApus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  const [bannerVisible, setBannerVisible] = useState(() => !localStorage.getItem('ohb.lexiconBannerDismissed'));
  const fetchedFor = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // markVisit only on first load (StrictMode-guarded); action refreshes never re-stamp.
  const loadAll = useCallback(async (markVisit) => {
    const g = (p, fb = []) => api.get(p).then((r) => r.data).catch(() => fb);
    const loadAttributions = () => api.get(`/attributions?household_id=${hid}`)
      .then((r) => (r.data || []).filter((row) => OPEN_ATTRIBUTION_STATES.includes(row.state)))
      .catch(() => {
        toast.error("Couldn't load attributions");
        return [];
      });
    const [s, ft, d, es, ev, rc, sd, attr, un, ae, ra] = await Promise.all([
      g(`/ledger/trust-summary?household_id=${hid}${markVisit ? "&mark_visit=true" : ""}`, null),
      g(`/fact-types`, []),
      g(`/documents?household_id=${hid}&received_after=${daysAgo(7)}`),
      g(`/expected_statements?household_id=${hid}&sort=-days_overdue`),
      g(`/reconciliation-events?household_id=${hid}&created_after=${daysAgo(7)}`),
      g(`/reconciliations?household_id=${hid}`),
      g(`/reconciliations/with-details?household_id=${hid}&state=suggested`),
      loadAttributions(),
      g(`/apus?household_id=${hid}&fact_type=uncategorized`),
      g(`/adapter-errors?household_id=${hid}&resolved=false`),
      g(`/apus?household_id=${hid}&trust_state=reconciled`),
    ]);
    if (markVisit) setSummary(s);  // keep previous_visit_at stable across action refreshes
    else setSummary((prev) => ({ ...s, previous_visit_at: prev?.previous_visit_at, changes_since: prev?.changes_since }));
    setFactLabels(buildFactLabels(ft)); setFactTypes(ft); setDocs(d); setAllES(es);
    setEvents(ev); setRecs(rc); setSuggestedDetailed(sd); setAttributions(attr); setUncategorized(un);
    setAdapterErrors(ae); setReconciledApus(ra);
  }, [hid]);

  useEffect(() => {
    if (!hid) return;
    if (fetchedFor.current === hid) return;
    fetchedFor.current = hid;
    (async () => { setLoading(true); await loadAll(true); setLoading(false); })();
  }, [hid, loadAll]);

  const propName = useMemo(() => {
    const m = {}; (assets || []).forEach((a) => { m[a.id] = a.name || a.address; }); return m;
  }, [assets]);
  const esById = useMemo(() => Object.fromEntries(allES.map((e) => [e.id, e])), [allES]);
  const overdueES = useMemo(() => allES.filter((e) => e.status === "overdue"), [allES]);
  const suggested = useMemo(() => recs.filter((r) => r.state === "suggested"), [recs]);
  const awaiting = useMemo(() => recs.filter((r) => r.un_match_reason === "awaiting_distribution"), [recs]);

  // S6 ready-for-CPA: (property, period-month) where every ES is received.
  const readyPeriods = useMemo(() => {
    const groups = {};
    allES.forEach((e) => {
      const key = `${e.property_id}|${(e.period?.start || "").slice(0, 7)}`;
      (groups[key] = groups[key] || []).push(e);
    });
    return Object.entries(groups)
      .filter(([, list]) => list.length > 0 && list.every((x) => x.status === "received"))
      .map(([key]) => {
        const [pid, month] = key.split("|");
        const count = reconciledApus.filter(
          (a) => a.lineage?.property_id === pid && (a.period?.start || "").slice(0, 7) === month).length;
        return { pid, month, count };
      });
  }, [allES, reconciledApus]);

  const openApu = (id) => { setDialog(null); setDrawer({ type: "apu", id }); };

  // Bite 4.5 deep-link routing: ?focus=section-N&entity=type:id → scroll + open drawer.
  useEffect(() => {
    if (loading) return;
    const focus = searchParams.get("focus");
    const entity = searchParams.get("entity");
    if (focus) {
      const map = { "section-2": "section-missing", "section-4": "section-exceptions",
        "section-5": "section-decision", "section-3": "section-changed", hero: "mc-hero" };
      setTimeout(() => scrollToSection(map[focus] || focus), 300);
    }
    if (entity) {
      const [type, id] = entity.split(":");
      if (type === "apu") setDrawer({ type: "apu", id });
      else if (type === "expected_statement") setDrawer({ type: "expected_statement", id, data: esById[id] || { id } });
      else if (type === "adapter_error") {
        const e = adapterErrors.find((x) => x.id === id);
        if (e) setDrawer({ type: "adapter_error", id, data: e });
      } else if (type === "reconciliation") {
        const r = recs.find((x) => x.id === id);
        if (r?.statement_apu_ids?.[0]) setDrawer({ type: "apu", id: r.statement_apu_ids[0] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const c = summary?.apu_counts || {};
  const total = c.total || 0;
  const reconciled = c.reconciled || 0;
  const pct = total ? Math.round((reconciled / total) * 100) : 0;
  const p = summary?.plaid || {};
  const aw = summary?.awaiting_distribution || {};
  const ph = summary?.pm_health || {};
  const silentN = (ph.silent_pms || []).length;
  const neverN = (ph.never_contacted_pms || []).length;
  const needsYou = (c.review || 0) + (c.disputed || 0) + suggested.length + attributions.length + adapterErrors.length + overdueES.length;

  const hasData = total > 0 || (p.items_total || 0) > 0 || docs.length > 0;

  // ── tile click → list dialog ──
  const apuListDialog = async (title, filter) => {
    const r = await api.get(`/apus?household_id=${hid}&include_superseded=false`).catch(() => ({ data: [] }));
    const rows = (r.data || []).filter(filter);
    setDialog({
      title, rows, emptyText: "No numbers match.", onRowClick: (row) => openApu(row.id),
      columns: [
        { primary: true, render: (x) => `${formatAPUValue(x.value, x.unit_of_value, true)} · ${factLabel(x.fact_type, factLabels)}` },
        { render: (x) => `${x.trust_state} · ${fmtDate(x.period?.start)}` },
      ],
    });
  };

  if (loading) {
    return <div className="py-24 text-center text-sm uppercase tracking-[0.3em] text-muted-foreground animate-pulse">Loading morning check…</div>;
  }

  return (
    <div className="space-y-8 animate-fade-up">
      {/* Header */}
      <div className="flex items-start gap-4 pb-6 border-b border-border">
        <div className="h-12 w-12 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center shadow-sm">
          <Sun size={22} weight="regular" />
        </div>
        <div>
          <p className="eyebrow">Owner Morning Check</p>
          <h1 className="page-title text-[40px] sm:text-[48px] mt-1" data-testid="page-title">Good morning</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            What arrived, what's missing, what changed, what doesn't reconcile, and what needs you — every number drills to its source.
          </p>
        </div>
      </div>

      <StatusStrip
        previousVisitAt={summary?.previous_visit_at}
        changesSince={summary?.changes_since}
        needsYou={needsYou}
        onNeedsYou={() => scrollToSection("section-decision")}
        onChanges={() => scrollToSection("section-changed")}
      />

      {!hasData && (
        <div className="bg-card border border-border rounded-xl p-8 text-center space-y-4" data-testid="mc-onboarding">
          <p className="font-serif text-xl">Your morning check will populate as you add accounts and upload statements.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => navigate("/real-estate")} data-testid="mc-cta-connect-bank"
              className="px-4 py-2 rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-sm font-semibold">Connect a bank</button>
            <button onClick={() => navigate("/ingest")} data-testid="mc-cta-upload"
              className="px-4 py-2 rounded-md border border-border text-sm font-semibold hover:bg-muted">Upload a statement</button>
          </div>
        </div>
      )}

      {/* Lexicon banner */}
      {bannerVisible && (
        <div className="rounded-sm border border-border bg-card/60 px-5 py-4 flex items-start gap-3" data-testid="lexicon-banner">
          <ShieldCheck size={20} className="mt-0.5 text-primary shrink-0" />
          <div className="flex-1">
            <p className="text-sm leading-relaxed">
              Every dollar here starts as a <strong>Claim</strong> — one source said so. It becomes a <strong>Fact</strong> when an independent second source agrees, usually your bank. Only Facts are safe to hand to your CPA or your heirs.
            </p>
          </div>
          <button
            onClick={() => { localStorage.setItem('ohb.lexiconBannerDismissed', '1'); setBannerVisible(false); }}
            className="text-xs text-muted-foreground hover:text-foreground shrink-0"
            data-testid="lexicon-banner-dismiss">
            Got it
          </button>
        </div>
      )}

      {/* Hero tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="mc-hero">
        <HeroTile testid="tile-apu-trust" icon={ShieldCheck} label="Claims & Facts" value={total}
          sub={`${reconciled} verified · ${c.review || 0} awaiting proof`} tone="neutral"
          onClick={() => apuListDialog("All numbers by status", () => true)} />
        <HeroTile testid="tile-reconciled-pct" icon={Percent} label="Verified" value={`${pct}%`}
          sub={`${reconciled} of ${total} numbers proven`} tone={reconciledTone(pct)}
          onClick={() => apuListDialog("Claims awaiting a second source", (a) => a.trust_state !== "reconciled")} />
        <HeroTile testid="tile-plaid-health" icon={Bank} label="Bank links" value={p.items_active || 0}
          sub={`${p.items_pending || 0} syncing · ${p.items_needs_reauth || 0} need re-login`}
          tone={(p.oldest_unsynced_days || 0) > 7 ? "destructive" : (p.items_needs_reauth || p.items_pending) ? "warning" : "success"}
          onClick={() => setDialog({
            title: "Bank connections", emptyText: "No connections.",
            rows: [...(p.pending_items || []).map((i) => ({ ...i, _s: "syncing" })), ...(p.needs_reauth_items || []).map((i) => ({ ...i, _s: "needs re-login" }))],
            columns: [
              { primary: true, render: (x) => x.institution_name || x.plaid_item_id },
              { render: (x) => `${x._s} · last sync ${fmtDate(x.last_synced_at) || "never"}` },
            ],
          })} />
        <HeroTile testid="tile-awaiting" icon={HandCoins} label="Money on the way" value={aw.open_count || 0}
          sub={aw.open_count ? `oldest ${aw.oldest_days || 0} days` : "All distributions received"}
          tone={(aw.open_count || 0) === 0 ? "success" : "warning"}
          onClick={() => setDialog({
            title: "Awaiting PM distribution (by PM)", emptyText: "Nothing awaiting.",
            rows: aw.by_pm || [],
            columns: [{ primary: true, render: (x) => x.pm_name }, { render: (x) => `${x.count} item(s) · oldest ${x.oldest_days}d` }],
          })} />
        <HeroTile testid="tile-pm-health" icon={UserList} label="Property managers" value={`${silentN}/${neverN}`}
          sub={`${silentN} silent · ${neverN} awaiting first statement`} tone={silentN ? "warning" : neverN ? "neutral" : "success"}
          onClick={() => setDialog({
            title: "Property manager health", emptyText: "No PMs tracked.",
            rows: [
              ...(ph.silent_pms || []).map((x) => ({ ...x, _state: "silent" })),
              ...(ph.never_contacted_pms || []).map((x) => ({ ...x, _state: "never_contacted" })),
            ],
            columns: [
              { primary: true, render: (x) => x.pm_name },
              { render: (x) => contactStateView(x._state, x.days_since_last_contact).label },
            ],
          })} />
        <HeroTile testid="tile-adapter-errors" icon={Warning} label="Needs your call" value={adapterErrors.length}
          sub="Documents we couldn't read on our own" tone={adapterErrors.length ? "destructive" : "success"}
          onClick={() => setDialog({
            title: "Conversion errors", emptyText: "No errors.",
            rows: adapterErrors, onRowClick: (row) => { setDialog(null); setDrawer({ type: "adapter_error", id: row.id, data: row }); },
            columns: [
              { primary: true, render: (x) => errorLabel(x.error_type) },
              { render: (x) => `${x.details?.operator || x.adapter} · ${x.details?.owner_code || x.bundle_id || ""}` },
            ],
          })} />
      </div>

      {/* Sections */}
      <div className="grid gap-5">
        <Section testid="section-arrived" icon={TrayArrowDown} title="What arrived" desc="Documents received in the last 7 days" count={docs.length} empty="No new documents this week.">
          {docs.map((d) => (
            <RowItem key={d.id} testid={`arrived-${d.id}`} onClick={() => setDrawer({ type: "document", id: d.id, data: d })}
              left={<><p className="text-sm font-semibold truncate">{d.original_filename || prettyFact(d.type)}</p>
                <p className="text-xs text-muted-foreground">{prettyFact(d.type)} · {d.pm_name || d.source_kind} · {fmtDate(d.received_at)}</p></>}
              right={<span className="text-xs text-muted-foreground tabular-nums">{d.apu_count || 0} numbers</span>} />
          ))}
        </Section>

        <Section testid="section-missing" icon={ClockCountdown} title="What is missing" desc="Overdue expected statements" count={overdueES.length} empty="All expected statements have arrived on schedule.">
          {overdueES.map((e) => (
            <RowItem key={e.id} testid={`missing-${e.id}`} onClick={() => setDrawer({ type: "expected_statement", id: e.id, data: e })}
              left={<><p className="text-sm font-semibold truncate">{propName[e.property_id] || e.property_id}</p>
                <p className="text-xs text-muted-foreground">{e.pm_id} · {fmtDate(e.period?.start)} · due {fmtDate(e.due_date)}</p></>}
              right={<Chip tone={overdueTone(e.days_overdue || 0)}>{e.days_overdue}d overdue</Chip>} />
          ))}
        </Section>

        <Section testid="section-changed" icon={ArrowsClockwise} title="What changed" desc="Promotions to Fact and other status changes in the last 7 days" count={events.length} empty="No changes in the last 7 days.">
          {events.map((e) => {
            const isES = !!e.expected_statement_id;
            return (
              <RowItem key={e.id} testid={`changed-${e.id}`}
                onClick={() => isES ? setDrawer({ type: "expected_statement", id: e.expected_statement_id, data: esById[e.expected_statement_id] || { id: e.expected_statement_id } })
                  : e.apu_id ? openApu(e.apu_id) : undefined}
                left={<><p className="text-sm font-semibold">{eventLabel(e.event_type || e.triggered_by)}</p>
                  <p className="text-xs text-muted-foreground">{trustLabel(e.from_state)} → {trustLabel(e.to_state)} · {e.triggered_by}{e.decided_by ? ` (${e.decided_by})` : ""} · {fmtDate(e.decided_at)}</p></>}
                right={<Chip tone={e.to_state === "reconciled" ? "success" : e.to_state === "disputed" || e.to_state === "stale" ? "destructive" : "warning"}>{trustLabel(e.to_state)}</Chip>} />
            );
          })}
        </Section>

        <Section testid="section-exceptions" icon={Scales} title="What doesn't add up" desc="Pending deposits and documents that need your call" count={awaiting.length + adapterErrors.length} empty="No open exceptions.">
          {awaiting.length > 0 && <p className="px-5 pt-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Awaiting PM distribution</p>}
          {awaiting.map((r) => (
            <RowItem key={r.id} testid={`exc-aw-${r.id}`} onClick={() => r.statement_apu_ids?.[0] && openApu(r.statement_apu_ids[0])}
              left={<><p className="text-sm font-semibold">Statement waiting for the bank to agree</p>
                <p className="text-xs text-muted-foreground">{r.statement_apu_ids?.length || 0} claim(s) on this statement · created {fmtDate(r.created_at)}</p></>}
              right={<Chip tone="warning">waiting on bank</Chip>} />
          ))}
          {adapterErrors.length > 0 && <p className="px-5 pt-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Documents that need your call</p>}
          {adapterErrors.map((er) => (
            <RowItem key={er.id} testid={`exc-err-${er.id}`} onClick={() => setDrawer({ type: "adapter_error", id: er.id, data: er })}
              left={<><p className="text-sm font-semibold">{errorLabel(er.error_type)}{er.details?.owner_code ? ` — ${er.details.owner_code}: assign owner` : ""}</p>
                <p className="text-xs text-muted-foreground">{er.details?.operator || er.adapter}{er.details?.delta_cents != null ? ` · Δ ${centsToUSD(er.details.delta_cents, true)}` : ""}</p></>}
              right={<Chip tone="destructive">error</Chip>} />
          ))}
        </Section>

        <Section testid="section-decision" icon={SealQuestion} title="What needs your decision" desc="Suggested matches and uncategorized lines — act in the drawer" count={suggestedDetailed.length + attributions.length + uncategorized.length} empty="Nothing needs your decision right now.">
          {suggestedDetailed.map((r) => (
            <RowItem key={r.id} testid={`dec-sug-${r.id}`} onClick={() => r.statement_apu_ids?.[0] && openApu(r.statement_apu_ids[0])}
              left={<><p className="text-sm font-semibold tabular-nums">{formatAPUValue(r.enriched?.statement_apu_value, "USD_cents", true)} · {r.enriched?.pm_name || prettyFact(r.enriched?.statement_apu_fact_type)}</p>
                <p className="text-xs text-muted-foreground">{r.enriched?.property_label || "—"} ↔ {r.enriched?.bank_account_label || "bank"} · Confirm / Reject in drawer</p></>}
              right={<Chip tone="warning">{((r.confidence_score || 0) * 100).toFixed(0)}%</Chip>} />
          ))}
          {attributions.map((a) => {
            const bank = a.enriched?.bank_apu || {};
            const msg = (a.enriched?.citing_messages || [])[0] || {};
            const snippet = attributionSnippet(a);
            const state = attributionState(a);
            const StateIcon = state.Icon;
            return (
              <RowItem key={a.id} testid={`attr-row-${a.id}`} onClick={() => setDrawer({ type: "attribution", id: a.id, data: a })}
                left={<>
                  <p className="text-sm font-semibold flex items-baseline gap-2 min-w-0">
                    <HoverCard openDelay={300}>
                      <HoverCardTrigger asChild>
                        <span
                          tabIndex={0}
                          data-testid={`attr-amount-trigger-${a.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-serif text-lg tabular-nums border-b border-dotted border-[#b08d57] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm shrink-0"
                        >
                          {formatAPUValue(Math.abs(bank.value ?? 0), bank.unit_of_value || "USD_cents")}
                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent className="max-w-sm w-80 border border-border shadow-sm rounded-md bg-card">
                        <blockquote className="rounded bg-background border-l-2 border-[#b08d57] px-3 py-2 text-sm text-foreground">
                          {snippet || <span className="text-muted-foreground">No snippet extracted.</span>}
                        </blockquote>
                        <p className="mt-3 text-xs text-muted-foreground truncate">{messageLine(msg)}</p>
                        <Chip tone={state.tone}><StateIcon size={12} /> {state.label}</Chip>
                      </HoverCardContent>
                    </HoverCard>
                    <span className="truncate">{state.phrase}</span>
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{messageLine(msg)}</p>
                </>}
                right={<Chip tone={state.tone}><StateIcon size={12} /> {state.label}</Chip>} />
            );
          })}
          {uncategorized.map((a) => (
            <RowItem key={a.id} testid={`dec-unc-${a.id}`} onClick={() => openApu(a.id)}
              left={<><p className="text-sm font-semibold tabular-nums">{formatAPUValue(a.value, a.unit_of_value, true)}</p>
                <p className="text-xs text-muted-foreground truncate">{(a.source?.raw_text || "uncategorized").slice(0, 80)} · Classify in drawer</p></>}
              right={<Chip tone="muted">uncategorized</Chip>} />
          ))}
        </Section>

        <Section testid="section-cpa" icon={CheckCircle} title="What is ready for CPA / export" desc="Months where every number is a Fact — safe to hand to your CPA" count={readyPeriods.length} empty="No months fully verified yet.">
          {readyPeriods.map((rp) => (
            <RowItem key={`${rp.pid}-${rp.month}`} testid={`cpa-${rp.pid}-${rp.month}`}
              onClick={() => apuListDialog(`${propName[rp.pid] || rp.pid} · ${rp.month}`,
                (a) => a.lineage?.property_id === rp.pid && (a.period?.start || "").slice(0, 7) === rp.month)}
              left={<><p className="text-sm font-semibold">{propName[rp.pid] || rp.pid}</p>
                <p className="text-xs text-muted-foreground">{rp.month}</p></>}
              right={<span className="text-xs text-muted-foreground tabular-nums">{rp.count} verified numbers</span>} />
          ))}
        </Section>
      </div>

      <ListDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}
        title={dialog?.title} rows={dialog?.rows} columns={dialog?.columns}
        onRowClick={dialog?.onRowClick} emptyText={dialog?.emptyText} />

      <ProvenanceDrawer entity={drawer} hid={hid} factLabels={factLabels} factTypes={factTypes} assets={assets} isMobile={isMobile}
        onClose={() => setDrawer(null)} onOpenApu={(id) => setDrawer({ type: "apu", id })}
        onAction={() => loadAll(false)} />
    </div>
  );
}
